// Relevant PDP recommendation rows, see sections/ms-related-products.liquid.
// Each row merges its candidates by priority — curated PIM list, Shopify recommendations, category
// bestsellers — and never repeats a product already shown by an earlier row.
// Every row includes this file, so it runs more than once: no top-level const/let/class.

// Algorithmic recommendations outside the product's sub-category (a recipe book for a pan) rank
// after the category bestsellers; curated ones carry no data-collections and are never demoted.
function relatedItemRank(item, category) {
  const source = item.dataset.source;
  const collections = item.dataset.collections;
  if (source === "recommendation" && category && collections && !collections.includes(`,${category},`)) {
    return "off-topic";
  }
  return source;
}

function pickRelatedItems(items, { limit, excludeIds, category = "" }) {
  const skip = new Set(excludeIds.map(String));
  const picked = [];
  for (const rank of ["pim", "recommendation", "category", "off-topic"]) {
    for (const item of items) {
      if (picked.length >= limit) return picked;
      const id = item.dataset.productId;
      if (relatedItemRank(item, category) !== rank || !id || skip.has(id)) continue;
      skip.add(id);
      picked.push(item);
    }
  }
  return picked;
}

if (!customElements.get("ms-related-products")) {
  customElements.define(
    "ms-related-products",
    class extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        // Rows upgrade in document order, so every earlier row already exposes `ready`.
        const rows = Array.from(document.querySelectorAll("ms-related-products"));
        const earlier = rows.slice(0, rows.indexOf(this)).map((row) => row.ready || Promise.resolve([]));
        this.ready = Promise.all(earlier)
          .then((shown) => this._render(shown.flat()))
          .catch(() => []);
      }

      async _render(shownByEarlierRows) {
        const grid = this.querySelector(".ms-related__grid");
        if (!grid) return [];

        const serverItems = Array.from(grid.children).filter((el) => el.classList.contains("ms-related__item"));
        const recommendations = await this._fetchRecommendations();
        const picked = pickRelatedItems(serverItems.concat(recommendations), {
          limit: Number(this.dataset.limit) || 4,
          excludeIds: shownByEarlierRows.concat(this.dataset.productId),
          category: this.dataset.category,
        });

        grid.replaceChildren(...picked);
        if (picked.length === 0) {
          const section = this.closest(".shopify-section");
          if (section) section.style.display = "none";
        }
        return picked.map((item) => item.dataset.productId);
      }

      _fetchRecommendations() {
        const intent = this.dataset.intent;
        if (!intent || intent === "none" || !this.dataset.url) return Promise.resolve([]);

        const params = new URLSearchParams({
          section_id: "ms-related-recommendations",
          product_id: this.dataset.productId,
          limit: "10",
          intent,
        });
        return fetch(`${this.dataset.url}?${params}`)
          .then((response) => (response.ok ? response.text() : ""))
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, "text/html");
            return Array.from(doc.querySelectorAll(".ms-related__item"));
          })
          .catch(() => []);
      }
    },
  );
}
